import { Autocomplete } from '@react-google-maps/api'
import React, { useEffect, useState } from 'react'
import Input from './input'
import useGoogleMapsLoader from './useGoogleMapsLoader'

export const allPossibleAroundKMValues = [10, 20, 30, 50, 100] as const

export type PlacePropsValue = {
  address?: string
  lat?: number
  lng?: number
  aroundKm?: (typeof allPossibleAroundKMValues)[number]
}

export type PlaceProps = {
  value?: PlacePropsValue
  placeholder?: string
  googleMapsApiKey: string

  // eslint-disable-next-line no-unused-vars
  onChange?: (value: PlacePropsValue) => void
  onClear?: () => void
}

const Place: React.FC<PlaceProps> = ({
  value = {},
  placeholder = '',
  googleMapsApiKey,
  onChange = undefined,
  onClear = undefined,
}) => {
  const [inputSearchValue, setInputSearchValue] = useState(value?.address || '')
  const [inputAroundValue, setInputAroundValue] = useState<
    PlacePropsValue['aroundKm']
  >(
    value?.aroundKm ||
      allPossibleAroundKMValues[allPossibleAroundKMValues.length - 1],
  )

  const isLoaded = useGoogleMapsLoader(googleMapsApiKey)

  const [autocomplete, setAutocomplete] = useState<
    google.maps.places.Autocomplete | undefined
  >()

  useEffect(() => {
    if (
      value?.address &&
      value.lat &&
      value.lng &&
      inputAroundValue &&
      onChange
    ) {
      onChange({ ...value, aroundKm: inputAroundValue })
    }
  }, [inputAroundValue])

  const aroundDistanceSelector = (
    <>
      <div className="w-px ml-1 h-1/2 bg-primary" />
      <select
        className="h-full bg-transparent border-0 outline-0 focus:ring-0"
        value={inputAroundValue}
        onChange={(event) => {
          setInputAroundValue(
            parseInt(event.target.value, 10) as PlacePropsValue['aroundKm'],
          )
        }}
      >
        {allPossibleAroundKMValues.map((aroundKMValue) => (
          <option key={aroundKMValue} value={aroundKMValue}>
            {`${aroundKMValue} km`}
          </option>
        ))}
      </select>
    </>
  )

  const input = (
    <Input
      icon="pin"
      value={inputSearchValue}
      onChange={(val) => setInputSearchValue(val)}
      onClear={() => {
        setInputSearchValue('')
        if (onChange) onChange({})
        if (onClear) onClear()
      }}
      placeholder={placeholder}
      spellCheck={false}
      autoCapitalize="none"
      autoComplete="off"
      append={aroundDistanceSelector}
    />
  )

  return isLoaded ? (
    <Autocomplete
      className="w-full"
      onLoad={(val) => setAutocomplete(val)}
      onPlaceChanged={() => {
        if (autocomplete) {
          const place = autocomplete.getPlace()
          setInputSearchValue(place.formatted_address || '')
          if (onChange) {
            onChange({
              address: place.formatted_address,
              lat: place.geometry?.location?.lat(),
              lng: place.geometry?.location?.lng(),
              aroundKm: inputAroundValue,
            })
          }
        } else {
          // eslint-disable-next-line no-console
          console.log('autocomplete not loaded')
        }
      }}
    >
      {input}
    </Autocomplete>
  ) : (
    input
  )
}

export default Place
